/* Tyres Direct V2: shared behaviour. No dependencies, ES2019, defer-safe.
   Contract: docs/v2/FOUNDATION_API.md
   Safe to load twice: the second copy returns at once. */
(function () {
  'use strict';

  var TD = (window.TD = window.TD || {});
  if (TD.__v2) return;
  TD.__v2 = true;
  TD.version = '2.1.0';

  var doc = document;

  /* ---------- small helpers ---------- */

  function emit(name, detail) {
    try {
      doc.dispatchEvent(new CustomEvent(name, { detail: detail }));
    } catch (e) {
      /* very old browser: ignore */
    }
  }

  TD.ready = function (fn) {
    if (doc.readyState === 'loading') {
      doc.addEventListener('DOMContentLoaded', fn, { once: true });
    } else {
      fn();
    }
  };

  /* Pence (integer) to "£1,234.56" */
  TD.money = function (pence) {
    var p = Math.round(Number(pence) || 0);
    var neg = p < 0;
    p = Math.abs(p);
    var pounds = String(Math.floor(p / 100)).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    var rest = p % 100;
    return (neg ? '-' : '') + '£' + pounds + '.' + (rest < 10 ? '0' : '') + rest;
  };

  /* ---------- analytics ---------- */

  TD.track = function (name, data) {
    var payload = data && typeof data === 'object' ? data : {};
    var evt = 'td_' + String(name || 'event');
    try {
      if (window.dataLayer && typeof window.dataLayer.push === 'function') {
        var row = { event: evt };
        for (var k in payload) {
          if (Object.prototype.hasOwnProperty.call(payload, k)) row[k] = payload[k];
        }
        window.dataLayer.push(row);
      }
    } catch (e) {
      /* never throw from tracking */
    }
    try {
      var sa = window.Shopify && window.Shopify.analytics;
      if (sa && typeof sa.publish === 'function') sa.publish(evt, payload);
    } catch (e) {
      /* never throw from tracking */
    }
  };

  /* ---------- fit context (sessionStorage td_fit) ---------- */

  var FIT_KEY = 'td_fit';
  var NUM_RE = /^[0-9]{1,3}(\.[0-9]{1,2})?$/;
  var LI_RE = /^[0-9]{2,3}(\/[0-9]{2,3})?$/;
  var SR_RE = /^[A-Za-z][0-9A-Za-z]?$/;
  /* Current UK plate format (AB12 CDE). Scrubbed from veh and url; nothing else
     is matched because model names such as "320D" or "C200" look like old plates. */
  var REG_RE = /\b[A-Za-z]{2}[0-9]{2}\s?[A-Za-z]{3}\b/g;
  var URL_DROP = /^(reg|vrm|registration|plate|number_?plate|reg_?number)$/i;

  function str(v, max) {
    if (v === null || v === undefined) return '';
    return String(v).trim().slice(0, max || 64);
  }
  function num(v) {
    v = str(v, 8);
    return NUM_RE.test(v) ? v : '';
  }
  function cleanUrl(v) {
    v = str(v, 2000);
    if (!v) return '';
    try {
      var u = new URL(v, window.location.origin);
      if (u.origin !== window.location.origin) return '';
      var drop = [];
      u.searchParams.forEach(function (val, key) {
        if (URL_DROP.test(key)) drop.push(key);
      });
      drop.forEach(function (key) {
        u.searchParams.delete(key);
      });
      var out = (u.pathname + u.search).replace(REG_RE, '');
      /* Never cut a URL mid-parameter (a long tier filter broke here): too long means no URL; the size is kept. */
      return out.length <= 1800 ? out : u.pathname;
    } catch (e) {
      return '';
    }
  }
  function cleanFit(o) {
    o = o || {};
    return {
      w: num(o.w),
      a: num(o.a),
      r: num(o.r),
      li: LI_RE.test(str(o.li, 8)) ? str(o.li, 8) : '',
      sr: SR_RE.test(str(o.sr, 3)) ? str(o.sr, 3).toUpperCase() : '',
      veh: str(o.veh, 80).replace(REG_RE, '').replace(/\s{2,}/g, ' ').trim(),
      url: cleanUrl(o.url),
      at: Date.now()
    };
  }

  var TDFit = {
    get: function () {
      try {
        var raw = window.sessionStorage.getItem(FIT_KEY);
        if (!raw) return null;
        var o = JSON.parse(raw);
        return o && typeof o === 'object' ? o : null;
      } catch (e) {
        return null;
      }
    },
    /* Replaces the stored fit. Only w, a, r, li, sr, veh, url are kept; at is set here. */
    set: function (obj) {
      var fit = cleanFit(obj);
      try {
        window.sessionStorage.setItem(FIT_KEY, JSON.stringify(fit));
      } catch (e) {
        /* storage blocked: still report the value */
      }
      emit('td:fit-change', fit);
      return fit;
    },
    clear: function () {
      try {
        window.sessionStorage.removeItem(FIT_KEY);
      } catch (e) {
        /* ignore */
      }
      emit('td:fit-change', null);
    },
    /* Reads the size from a results URL. Returns {w, a, r, li, sr}; '' when absent. */
    fromUrl: function (url) {
      var out = { w: '', a: '', r: '', li: '', sr: '' };
      try {
        var u = new URL(url || window.location.href, window.location.origin);
        var p = u.searchParams;
        out.w = num(p.get('filter.p.m.custom.width'));
        out.a = num(p.get('filter.p.m.custom.aspect_ratio'));
        out.r = num(p.get('filter.p.m.custom.rim_diameter'));
        var li = str(p.get('filter.p.m.custom.load_index'), 8);
        var sr = str(p.get('filter.p.m.custom.speed_rating'), 3);
        out.li = LI_RE.test(li) ? li : '';
        out.sr = SR_RE.test(sr) ? sr.toUpperCase() : '';
        if (!out.r) {
          var m = u.pathname.match(/\/collections\/([0-9]{1,2}(?:-[0-9])?)-inch-tyres\/?$/);
          if (m) out.r = m[1].replace('-', '.');
        }
      } catch (e) {
        /* bad URL: empty result */
      }
      return out;
    },
    /* Stores the size from the current URL when it has a width and a rim.
       Keeps veh when the stored fit is the same size. Returns the stored fit or null. */
    capture: function (extra) {
      var f = TDFit.fromUrl();
      if (!f.w || !f.r) return null;
      var prev = TDFit.get();
      var next = { w: f.w, a: f.a, r: f.r, li: f.li, sr: f.sr, veh: '', url: window.location.pathname + window.location.search };
      if (prev && TDFit.same(prev, next)) next.veh = prev.veh || '';
      if (extra && typeof extra === 'object') {
        for (var k in extra) {
          if (Object.prototype.hasOwnProperty.call(extra, k)) next[k] = extra[k];
        }
      }
      return TDFit.set(next);
    },
    /* "205/55 R16"; aspect 0 or blank gives "185 R14". '' without width and rim. */
    label: function (f) {
      if (!f || !f.w || !f.r) return '';
      var a = f.a && Number(f.a) !== 0 ? '/' + f.a : '';
      return f.w + a + ' R' + f.r;
    },
    /* Numeric comparison of width, aspect and rim. Width must be set on both sides and equal.
       A blank aspect or rim on either side means "any" for that part; "0" is a real aspect
       (no profile, e.g. 185 R14) and must match as a number. */
    same: function (x, y) {
      if (!x || !y) return false;
      function blank(v) {
        return v === undefined || v === null || String(v).trim() === '';
      }
      function n(v) {
        var p = parseFloat(v);
        return isNaN(p) ? 0 : p;
      }
      function part(k) {
        return blank(x[k]) || blank(y[k]) || n(x[k]) === n(y[k]);
      }
      return n(x.w) > 0 && n(x.w) === n(y.w) && part('a') && part('r');
    }
  };
  window.TDFit = TDFit;

  /* ---------- scroll lock and chat ---------- */

  TD.closeChat = function () {
    try {
      var c = doc.querySelector('shopify-chat');
      if (c && c.open && typeof c.close === 'function') c.close();
    } catch (e) {
      /* ignore */
    }
  };

  TD.lockScroll = function (on) {
    var b = doc.body;
    if (!b) return;
    if (on) {
      b.classList.add('td-drawer-open');
      emit('td:drawer-open');
    } else {
      b.classList.remove('td-drawer-open');
      emit('td:drawer-close');
    }
  };

  ['cart-drawer:open', 'mobile-menu:open', 'td:drawer-open'].forEach(function (name) {
    doc.addEventListener(name, TD.closeChat);
  });

  /* ---------- chat launcher clearance (brief OVERLAY-01) ----------
     The Shopify Inbox launcher floats bottom right over whatever is there, at every width. While
     a purchase control sits under it, body.td-chat-tuck hides the launcher (td-v2.css): a button,
     select or quantity input of an add-to-basket form, quantity chips, a result row's buy block,
     price, Compare and "View tyre", the quick picks, the product page's express checkout and
     subtotal, a featured range price, any payment marks, the Fast Lane quantity steppers, the basket's
     Checkout and Remove, or anything marked
     [data-td-chat-clear]. It comes back as
     soon as the control scrolls away. An open chat panel is never hidden. */
  var CLEAR_SEL =
    'form[action*="/cart/add"] button, form[action*="/cart/add"] select, ' +
    'form[action*="/cart/add"] input:not([type="hidden"]), [data-td-qty] button, ' +
    '.td-row__form, .td-row__view, .td-row__cmp, .td-row__buy .td-price, .td-res__picks a, .td-pdp__express, ' +
    '.td-pdp__subline, .tdw__range-p, ' +
    '.td-footer__payments, [data-td-chat-clear], [data-add], .pay, .ftr .meta, [data-qty] button, .bk-sum .btn, .ln .rm';
  var tuckRaf = 0;
  function launcherRect(c) {
    try {
      var sr = c.shadowRoot;
      var a = sr && (sr.querySelector('.activator') || sr.querySelector('button'));
      /* Newer Inbox builds nest the launcher one shadow root deeper (shopify-agent-runtime) */
      if (!a && sr) {
        var inner = sr.querySelector('shopify-agent-runtime');
        var isr = inner && inner.shadowRoot;
        a = isr && (isr.querySelector('.activator') || isr.querySelector('[part~="activator"]') || isr.querySelector('button'));
      }
      if (a) {
        var r = a.getBoundingClientRect();
        if (r.width > 0 && r.height > 0) return r;
      }
    } catch (e) {
      /* closed or not yet rendered: use the usual corner */
    }
    var w = window.innerWidth;
    var h = window.innerHeight;
    return { left: w - 96, right: w - 8, top: h - 110, bottom: h - 8 };
  }
  function checkTuck() {
    tuckRaf = 0;
    var b = doc.body;
    if (!b) return;
    var on = false;
    try {
      var c = doc.querySelector('shopify-chat');
      if (c && !c.open) {
        var lr = launcherRect(c);
        var pad = 8;
        var els = doc.querySelectorAll(CLEAR_SEL);
        for (var i = 0; i < els.length; i++) {
          var r = els[i].getBoundingClientRect();
          if (!r.width || !r.height) continue;
          if (r.right > lr.left - pad && r.left < lr.right + pad && r.bottom > lr.top - pad && r.top < lr.bottom + pad) {
            on = true;
            break;
          }
        }
      }
    } catch (e) {
      on = false;
    }
    b.classList.toggle('td-chat-tuck', on);
  }
  TD.checkChatClearance = function () {
    /* A short timer rather than requestAnimationFrame: rAF does not run in a page that is not
       being painted, and a pending frame would then block every later check. */
    if (!tuckRaf) tuckRaf = setTimeout(checkTuck, 40);
  };

  /* ---------- sticky buy bar ---------- */

  TD.observeBuybar = function (el) {
    if (!el) return function () {};
    var root = doc.documentElement;
    var ro = null;
    function update() {
      var h = el.offsetHeight || 0;
      root.style.setProperty('--td-buybar-h', h + 'px');
      if (doc.body) doc.body.classList.toggle('td-has-buybar', h > 0);
    }
    if ('ResizeObserver' in window) {
      ro = new ResizeObserver(update);
      ro.observe(el);
    } else {
      window.addEventListener('resize', update);
    }
    update();
    return function disconnect() {
      if (ro) ro.disconnect();
      window.removeEventListener('resize', update);
      root.style.removeProperty('--td-buybar-h');
      if (doc.body) doc.body.classList.remove('td-has-buybar');
    };
  };

  /* ---------- quantity chips ---------- */
  /* <div data-td-qty-scope data-td-unit-price="5449">
       <div class="td-qty" data-td-qty data-td-qty-target="[name=quantity]" role="group" aria-label="Quantity">
         <button type="button" class="td-chip" data-td-qty-value="1" aria-pressed="false">1</button> ...
       </div>
       <input name="quantity" value="1"> <span data-td-subtotal></span>
     </div> */

  function scopeOf(el) {
    return (el && el.closest('[data-td-qty-scope]')) || doc;
  }
  function targetOf(group) {
    var sel = group.getAttribute('data-td-qty-target');
    if (!sel) return null;
    try {
      return scopeOf(group).querySelector(sel) || doc.querySelector(sel);
    } catch (e) {
      return null;
    }
  }
  function unitPrice(el, scope) {
    var v = el.getAttribute('data-td-unit-price');
    if (v === null && scope && scope.getAttribute) v = scope.getAttribute('data-td-unit-price');
    var n = parseInt(v, 10);
    return isNaN(n) ? 0 : n;
  }
  function syncGroup(group) {
    var input = targetOf(group);
    if (!input) return;
    var qty = parseInt(input.value, 10);
    if (isNaN(qty) || qty < 1) qty = 1;
    var btns = group.querySelectorAll('[data-td-qty-value]');
    for (var i = 0; i < btns.length; i++) {
      var v = parseInt(btns[i].getAttribute('data-td-qty-value'), 10);
      btns[i].setAttribute('aria-pressed', v === qty ? 'true' : 'false');
    }
    var scope = scopeOf(group);
    if (scope === doc) return;
    var subs = scope.querySelectorAll('[data-td-subtotal]');
    for (var j = 0; j < subs.length; j++) {
      var unit = unitPrice(subs[j], scope) || unitPrice(group, null);
      if (unit > 0) subs[j].textContent = TD.money(unit * qty);
    }
  }
  function groupsFor(input) {
    var out = [];
    var groups = doc.querySelectorAll('[data-td-qty]');
    for (var i = 0; i < groups.length; i++) {
      if (targetOf(groups[i]) === input) out.push(groups[i]);
    }
    return out;
  }

  TD.initQty = function (root) {
    var groups = (root || doc).querySelectorAll('[data-td-qty]');
    for (var i = 0; i < groups.length; i++) syncGroup(groups[i]);
  };

  doc.addEventListener('click', function (e) {
    var btn = e.target && e.target.closest ? e.target.closest('[data-td-qty-value]') : null;
    if (!btn) return;
    var group = btn.closest('[data-td-qty]');
    if (!group) return;
    var input = targetOf(group);
    if (!input || btn.disabled) return;
    e.preventDefault();
    var v = parseInt(btn.getAttribute('data-td-qty-value'), 10);
    if (isNaN(v) || v < 1) return;
    input.value = String(v);
    syncGroup(group);
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });

  function onInput(e) {
    var t = e.target;
    if (!t || !('value' in t)) return;
    var gs = groupsFor(t);
    for (var i = 0; i < gs.length; i++) syncGroup(gs[i]);
  }
  doc.addEventListener('change', onInput);
  doc.addEventListener('input', onInput);

  /* ---------- dispatch countdown ----------
     Facts are rendered once on <body> by layout/theme.liquid:
       data-td-countdown="true|false"  (settings.td_countdown_enabled)
       data-td-cutoff="13:00"          (settings.td_fact_dispatch_cutoff, 24h, London time)
       data-td-holidays="2026-12-25,..." (settings.td_bank_holidays, ISO dates)
     TD.dispatch(now?) returns
       {mode:'today', msLeft, cutoff:'13:00'}             before the cut-off on a working day
       {mode:'next', date:'Thursday 15 October', iso:'2026-10-15', tomorrow:true|false} otherwise
       null when the countdown is switched off or the cut-off cannot be read.
     Working day = Monday to Friday, not a listed bank holiday, judged on London wall-clock time. */

  var LONDON = 'Europe/London';
  var WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  var MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  var londonFmt = null;

  function pad2(n) {
    return (n < 10 ? '0' : '') + n;
  }
  function factsEl() {
    return doc.body || doc.documentElement;
  }
  function readFacts() {
    var el = factsEl();
    var on = el && el.getAttribute('data-td-countdown');
    var cut = (el && el.getAttribute('data-td-cutoff')) || '';
    var hol = (el && el.getAttribute('data-td-holidays')) || '';
    var m = /^([01]?[0-9]|2[0-3]):([0-5][0-9])$/.exec(cut.trim());
    var set = {};
    hol.split(/[\s,]+/).forEach(function (d) {
      if (/^\d{4}-\d{2}-\d{2}$/.test(d)) set[d] = true;
    });
    return {
      on: on === 'true',
      cutMin: m ? parseInt(m[1], 10) * 60 + parseInt(m[2], 10) : -1,
      cutoff: m ? pad2(parseInt(m[1], 10)) + ':' + m[2] : '',
      holidays: set
    };
  }
  /* London wall-clock parts of a Date: {y, mo (1-12), d, h, mi, s, ms} */
  function londonParts(date) {
    try {
      if (!londonFmt) {
        londonFmt = new Intl.DateTimeFormat('en-GB', {
          timeZone: LONDON,
          year: 'numeric',
          month: 'numeric',
          day: 'numeric',
          hour: 'numeric',
          minute: 'numeric',
          second: 'numeric',
          hourCycle: 'h23'
        });
      }
      var o = {};
      londonFmt.formatToParts(date).forEach(function (p) {
        if (p.type !== 'literal') o[p.type] = parseInt(p.value, 10);
      });
      return { y: o.year, mo: o.month, d: o.day, h: o.hour % 24, mi: o.minute, s: o.second, ms: date.getMilliseconds() };
    } catch (e) {
      /* no Intl time zone support: treat the device clock as UK time */
      return { y: date.getFullYear(), mo: date.getMonth() + 1, d: date.getDate(), h: date.getHours(), mi: date.getMinutes(), s: date.getSeconds(), ms: date.getMilliseconds() };
    }
  }
  /* Calendar helpers on a plain y/m/d, independent of any time zone */
  function dayInfo(y, mo, d) {
    var t = new Date(Date.UTC(y, mo - 1, d));
    return {
      y: t.getUTCFullYear(),
      mo: t.getUTCMonth() + 1,
      d: t.getUTCDate(),
      wd: t.getUTCDay(),
      iso: t.getUTCFullYear() + '-' + pad2(t.getUTCMonth() + 1) + '-' + pad2(t.getUTCDate())
    };
  }
  function isWorking(day, holidays) {
    return day.wd >= 1 && day.wd <= 5 && !holidays[day.iso];
  }

  TD.dispatch = function (now) {
    var f = readFacts();
    if (!f.on || f.cutMin < 0) return null;
    var date = now instanceof Date ? now : new Date();
    var p = londonParts(date);
    if (!p.y) return null;
    var today = dayInfo(p.y, p.mo, p.d);
    var nowMs = ((p.h * 60 + p.mi) * 60 + p.s) * 1000 + p.ms;
    var cutMs = f.cutMin * 60000;
    if (isWorking(today, f.holidays) && nowMs < cutMs) {
      return { mode: 'today', msLeft: cutMs - nowMs, cutoff: f.cutoff };
    }
    for (var i = 1; i <= 21; i++) {
      var day = dayInfo(p.y, p.mo, p.d + i);
      if (isWorking(day, f.holidays)) {
        return {
          mode: 'next',
          date: WEEKDAYS[day.wd] + ' ' + day.d + ' ' + MONTHS[day.mo - 1],
          iso: day.iso,
          tomorrow: i === 1
        };
      }
    }
    return null;
  };

  /* "Order within 2h 14m for same-day dispatch" / "Order now for dispatch on Thursday 15 October".
     Minutes are rounded down, so the promise is never longer than the real time left.
     '' when TD.dispatch() is null. */
  TD.countdownText = function (d) {
    if (d === undefined) d = TD.dispatch();
    if (!d) return '';
    if (d.mode === 'today') {
      var mins = Math.floor(d.msLeft / 60000);
      if (mins < 1) return 'Order now for same-day dispatch';
      var h = Math.floor(mins / 60);
      var m = mins % 60;
      var left = h > 0 ? h + 'h ' + m + 'm' : m + 'm';
      return 'Order within ' + left + ' for same-day dispatch';
    }
    return 'Order now for dispatch on ' + d.date;
  };

  /* Fills every [data-td-dispatch] element with the countdown text and keeps it current
     (one shared timer, on the minute). The element is hidden when there is no countdown.
     Optional data-td-dispatch-target=".selector" writes the text into a child instead. */
  var dispatchTimer = 0;
  function paintDispatch() {
    var els = doc.querySelectorAll('[data-td-dispatch]');
    if (!els.length) return;
    var d = TD.dispatch();
    var txt = TD.countdownText(d);
    for (var i = 0; i < els.length; i++) {
      var el = els[i];
      var sel = el.getAttribute('data-td-dispatch-target');
      var out = sel ? el.querySelector(sel) : el;
      if (!out) continue;
      if (out.textContent !== txt) out.textContent = txt;
      el.hidden = !txt;
      el.setAttribute('data-td-dispatch-mode', d ? d.mode : 'off');
    }
  }
  function scheduleDispatch() {
    clearTimeout(dispatchTimer);
    var ms = 60000 - (Date.now() % 60000) + 50;
    dispatchTimer = setTimeout(function () {
      paintDispatch();
      scheduleDispatch();
    }, ms);
  }
  TD.initDispatch = function () {
    paintDispatch();
    scheduleDispatch();
  };
  doc.addEventListener('visibilitychange', function () {
    if (!doc.hidden) TD.initDispatch();
  });

  doc.addEventListener('shopify:section:load', function (e) {
    TD.initQty(e.target);
    TD.initDispatch();
  });
  /* The basket drawer re-renders its content (scripts.js, section "header-mini-cart-content")
     after every add, quantity change or removal: fill its countdown straight away. */
  function watchMiniCart() {
    var box = doc.getElementById('header-mini-cart-content');
    if (!box || !('MutationObserver' in window)) return;
    var t = 0;
    new MutationObserver(function () {
      clearTimeout(t);
      t = setTimeout(paintDispatch, 30);
    }).observe(box, { childList: true, subtree: true });
  }
  doc.addEventListener('cart-drawer:open', paintDispatch);
  TD.ready(function () {
    TD.initQty(doc);
    TD.initDispatch();
    watchMiniCart();
    /* Chat clearance: on scroll and resize, and again while the Inbox widget loads late. */
    window.addEventListener('scroll', TD.checkChatClearance, { passive: true });
    window.addEventListener('resize', TD.checkChatClearance);
    doc.addEventListener('td:drawer-close', TD.checkChatClearance);
    [0, 400, 1500, 4000, 8000].forEach(function (ms) {
      setTimeout(TD.checkChatClearance, ms);
    });
  });
})();
